import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Camera, Database, KeyRound, Layers, ListFilter } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, fmtNum } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingRows, Metric, MetricGrid, PageHeader } from "@/components/shared";
import KvExplorer from "@/components/db/KvExplorer";
import VectorCollections from "@/components/db/VectorCollections";
import ApiKeys from "@/components/db/ApiKeys";
import ApiDocs from "@/components/db/ApiDocs";
import IndexPanel from "@/components/db/IndexPanel";
import SnapshotPanel from "@/components/db/SnapshotPanel";

const TABS = [
  { id: "kv", label: "Explorador KV", icon: Database },
  { id: "index", label: "Índice", icon: ListFilter },
  { id: "vectors", label: "Vetores", icon: Layers },
  { id: "keys", label: "Chaves API", icon: KeyRound },
  { id: "snapshots", label: "Snapshots", icon: Camera },
  { id: "docs", label: "API", icon: BookOpen },
];

export default function DatabaseDetail() {
  const { name } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "kv";
  const [db, setDb] = useState(null);

  const load = useCallback(() => api.get(`/databases/${name}`).then((r) => setDb(r.data)).catch((e) => toast.error(errMsg(e))), [name]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="database-detail-page">
      <Link to="/databases" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4" data-testid="back-to-databases">
        <ArrowLeft className="h-4 w-4" /> Bancos
      </Link>
      <PageHeader overline="Banco de dados" title={<span className="font-mono">{name}</span>} />

      {!db ? (
        <LoadingRows />
      ) : (
        <>
          <MetricGrid>
            <Metric label="Chaves (aprox.)" value={fmtNum(db.keys_approx)} hint={`${db.kv.sst_count} SSTables · ${fmtNum(db.kv.mem_entries)} na memtable${db.kv.tombstones ? ` · ${fmtNum(db.kv.tombstones)} apagadas aguardando limpeza` : ""}`} testId="db-metric-keys" />
            <Metric label="Vetores" value={fmtNum(db.vectors)} hint={`${db.collections} coleções`} testId="db-metric-vectors" />
            <Metric label="Disco" value={fmtBytes(db.disk_bytes)} hint={`dados ${fmtBytes(db.kv.sst_bytes)} · índice ${fmtBytes(db.index?.bytes || 0)}`} testId="db-metric-disk" />
            <Metric label="Chaves API" value={fmtNum(db.api_keys)} accent testId="db-metric-keys-count" />
          </MetricGrid>

          <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })} className="mt-8">
            <TabsList className="bg-transparent border-b border-zinc-800 rounded-none w-full justify-start h-auto p-0 gap-1 overflow-x-auto">
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-600 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none text-zinc-400 px-4 py-3 gap-2">
                  <t.icon className="h-4 w-4" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="kv" className="mt-6"><KvExplorer db={name} onChange={load} /></TabsContent>
            <TabsContent value="index" className="mt-6"><IndexPanel db={name} index={db.index} onChange={load} /></TabsContent>
            <TabsContent value="vectors" className="mt-6"><VectorCollections db={name} onChange={load} /></TabsContent>
            <TabsContent value="keys" className="mt-6"><ApiKeys db={name} keys={db.api_keys_list} onChange={load} /></TabsContent>
            <TabsContent value="snapshots" className="mt-6"><SnapshotPanel db={name} onChange={load} /></TabsContent>
            <TabsContent value="docs" className="mt-6"><ApiDocs db={name} apiKey={db.api_keys_list?.[0]?.key} collections={db.collections_list} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
